import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext } from 'react';
import Button from '@ids-ts/button';
import GlobalNavigation from './GlobalNavigation';
import HofEndedPage from './HofEndedPage';
import styles from './styles/Flow.module.scss';
const HofEndedContext = createContext(false);
export const useIsHofEnded = () => useContext(HofEndedContext);
/**
 * Determines the CSS class for a step indicator based on its relation to current step
 * @param {number} step - The step number
 * @param {number} currentStep - The current active step
 * @param {number} totalSteps - Total number of steps
 * @returns {string} CSS class name
 */
const getStepClass = (step, currentStep, totalSteps) => {
    // If we're on the last step (success page), mark all steps as completed
    if (currentStep + 1 >= totalSteps) {
        return styles.completed;
    }
    // Otherwise, mark current step as active and previous steps as completed
    if (step === currentStep + 1)
        return styles.active;
    if (step < currentStep + 1)
        return styles.completed;
    return '';
};
/**
 * Base Flow component that provides the container structure
 * for multi-step flows with responsive layout
 * @param {FlowProps} props - Component props
 * @param {ReactNode} props.children - Child components to render
 * @param {number} props.currentStep - Current step number
 * @param {number} props.totalSteps - Total number of steps
 * @param {string} props.stepTitle - Title for the current step
 * @param {Function} props.onNext - Callback for Next button
 * @param {Function} props.onBack - Callback for Back button
 * @param {boolean} props.isNextDisabled - Whether Next button is disabled
 * @param {string} props.nextButtonLabel - Label for Next button
 * @param {string} props.backButtonLabel - Label for Back button
 * @param {boolean} props.hideNavigation - Whether to hide navigation buttons
 * @returns {JSX.Element} Flow component
 */
const Flow = ({ children, currentStep, totalSteps, stepTitle = '', onNext, onBack, isNextDisabled = false, nextButtonLabel = 'Next', backButtonLabel = 'Back', hideNavigation = false, hideStepIndicators = false, }) => {
    const HOF_END_DATE = new Date('2026-04-14T23:59:00-08:00');
    const isHofEnded = Date.now() >= HOF_END_DATE.getTime();
    const steps = Array.from({ length: totalSteps }, (_, i) => i + 1);
    // Separate completed steps (go on left) from active/future steps (go on right)
    const completedSteps = steps.filter((step) => step < currentStep + 1);
    const remainingSteps = steps.filter((step) => step >= currentStep + 1);
    if (isHofEnded) {
        return (_jsx(HofEndedContext.Provider, { value: isHofEnded, children: _jsx(HofEndedPage, {}) }));
    }
    return (_jsx(HofEndedContext.Provider, { value: isHofEnded, children: _jsxs("div", { className: styles.flowContainer, children: [_jsx(GlobalNavigation, {}), _jsxs("main", { className: styles.flowContent, children: [!hideStepIndicators && (_jsx("div", { className: styles.stepIndicators, children: completedSteps.map((step) => (_jsx("div", { className: `${styles.stepIndicator} ${styles.completed}`, "aria-label": `Step ${step}`, children: _jsx("span", { className: styles.stepNumber, children: step }) }, step))) })), _jsxs("div", { className: styles.contentWrapper, "data-step": currentStep, "data-total-steps": totalSteps, children: [stepTitle && (_jsxs("div", { className: styles.headerSection, children: [_jsxs("p", { className: styles.stepLabel, children: ["Step ", currentStep + 1] }), _jsx("h1", { className: styles.title, children: stepTitle })] })), _jsx("div", { className: styles.stepContent, children: children }), !hideNavigation && (onNext || onBack) && (_jsxs("div", { className: styles.navigationButtons, children: [onBack && currentStep > 0 && (_jsx(Button, { className: styles.backButton, onClick: onBack, "aria-label": "Go back to previous step", children: backButtonLabel })), onNext && currentStep < totalSteps && (_jsx(Button, { className: styles.nextButton, onClick: onNext, disabled: isNextDisabled, "aria-label": "Continue to next step", children: nextButtonLabel }))] }))] }), !hideStepIndicators && (_jsx("div", { className: styles.stepIndicators, children: remainingSteps.map((step) => (_jsx("div", { className: `${styles.stepIndicator} ${getStepClass(step, currentStep, totalSteps)}`, "aria-label": `Step ${step}`, "aria-current": step === currentStep + 1 ? 'step' : undefined, children: _jsx("span", { className: styles.stepNumber, children: step }) }, step))) }))] })] }) }));
};
export default Flow;
